import React, { useId, useMemo } from "react";
import { twJoin } from "tailwind-merge";

import { ChartPatterns } from "@/components/Charts/ChartPatterns";
import { ChartTooltip } from "@/components/Charts/ChartTooltip";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { ANALYTICS_LABELED_Y_AXIS_WIDTH } from "@/modules/analytics/constants";
import { useTranslation } from "@/translations";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "@/ui-adapters/Recharts";

export interface BarChartDataPoint {
  x: Date | string;
  y: number | undefined;
  isPending?: boolean;
}

type Props<TPrimary extends BarChartDataPoint, TSecondary extends BarChartDataPoint> = {
  dataPrimary: {
    data: TPrimary[];
    color?: string;
    onClick?: (dataPoint: TPrimary) => void;
  };
  dataSecondary?: {
    data: TSecondary[];
    color?: string;
    onClick?: (dataPoint: TSecondary) => void;
  };
  dataBenchmark?: BarChartDataPoint[];
  xLabel?: string;
  yLabel?: string;
  hoverLabel?: (
    bar: { x: string; primary?: TPrimary; secondary?: TSecondary; benchmark?: BarChartDataPoint },
    isSecondary: boolean,
  ) => React.ReactNode;
  formatYTick?: (y: number) => string;
  yTickMode?: "integer" | "auto";
  maxY?: number;
  heightAuto?: boolean;
} & (
  | {
      xAxisType: "date";
      formatXTick?: never;
    }
  | {
      xAxisType?: never;
      formatXTick?: (x: string) => string;
    }
);

const MAX_Y_TICKS = 6;

/** Renders one or two analytics bar series whose data points share the same X/Y contract. */
export function BarChart<TPrimary extends BarChartDataPoint, TSecondary extends BarChartDataPoint>({
  dataPrimary,
  dataSecondary,
  dataBenchmark,
  xAxisType,
  xLabel,
  yLabel,
  formatXTick,
  formatYTick,
  hoverLabel,
  yTickMode = "auto",
  maxY,
  heightAuto,
}: Props<TPrimary, TSecondary>): React.ReactNode {
  const { i18n } = useTranslation();
  const patternIdPrimary = useId();
  const patternIdSecondary = useId();
  const colorPrimary = dataPrimary.color || dataColors.primary;
  const colorSecondary = dataSecondary?.color || dataColors.green;
  const barWidth = dataSecondary && dataSecondary.data.length > 10 ? 12 : 16;

  const graphData = useMemo(() => {
    const listX = dataPrimary.data.map((point) => point.x);

    if (xAxisType === "date") {
      listX.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    }

    return listX.map((x) => {
      const primaryPoint = dataPrimary.data.find((d) => d.x === x);
      const secondaryPoint = dataSecondary?.data.find((d) => d.x === x);
      const benchmarkPoint = dataBenchmark?.find((d) => d.x === x);

      return {
        x,
        primary: primaryPoint,
        secondary: secondaryPoint,
        benchmark: benchmarkPoint,
        primaryY: primaryPoint?.y,
        secondaryY: secondaryPoint?.y,
        benchmarkY: benchmarkPoint?.y,
      };
    });
  }, [dataBenchmark, dataPrimary.data, dataSecondary?.data, xAxisType]);

  const maxYInData = useMemo(() => {
    let maxYValue = 0;

    graphData.forEach((dataPoint) => {
      if (dataPoint.primaryY !== undefined && dataPoint.primaryY > maxYValue) {
        maxYValue = dataPoint.primaryY;
      }
      if (dataPoint.secondaryY !== undefined && dataPoint.secondaryY > maxYValue) {
        maxYValue = dataPoint.secondaryY;
      }
      if (dataPoint.benchmarkY !== undefined && dataPoint.benchmarkY > maxYValue) {
        maxYValue = dataPoint.benchmarkY;
      }
    });

    return maxYValue;
  }, [graphData]);
  const maxYOverride = maxY ?? maxYInData;

  return (
    <ResponsiveContainer minHeight={200} className={twJoin("flex-1", heightAuto && "h-auto!")}>
      <ComposedChart
        className="no-top-bottom-lines"
        data={graphData}
        margin={{
          top: 24,
          right: xLabel ? 8 : 0,
          bottom: xLabel ? 32 : 0,
        }}
      >
        <ChartPatterns.Stripes id={patternIdPrimary} color={colorPrimary} />
        <ChartPatterns.Stripes id={patternIdSecondary} color={colorSecondary} />

        <CartesianGrid stroke={dataColors.grid} vertical={false} />
        <XAxis
          dataKey="x"
          axisLine={false}
          tickLine={false}
          tickFormatter={
            formatXTick ||
            (xAxisType === "date" ? (value: string) => formatDate(i18n, "monthYearShort", value) : undefined)
          }
          label={xLabel && <Label value={xLabel} position="bottom" fontSize={14} fill={dataColors.label} />}
          fontSize={12}
        />
        <YAxis
          width={yLabel ? ANALYTICS_LABELED_Y_AXIS_WIDTH : undefined}
          domain={[0, maxY ?? "auto"]}
          axisLine={false}
          tickLine={false}
          tickCount={yTickMode === "integer" && maxYOverride <= MAX_Y_TICKS ? maxYOverride + 1 : MAX_Y_TICKS}
          tickFormatter={formatYTick || ((val) => String(Math.round(val * 10) / 10))}
          padding={{ top: 20, bottom: 10 }}
          label={
            yLabel && (
              <Label
                value={yLabel}
                position="left"
                angle={-90}
                offset={-15}
                textAnchor="middle"
                fontSize={14}
                fill={dataColors.label}
              />
            )
          }
          fontSize={12}
        />

        <Tooltip cursor={{ fill: "red" }} content={<CustomTooltip type={xAxisType} hoverLabel={hoverLabel} />} />

        <Bar
          dataKey="primaryY"
          barSize={barWidth}
          shape={(props) => {
            const { primary, ...rectProps } = props as React.ComponentProps<typeof Rectangle> & {
              primary?: TPrimary;
            };

            return (
              <Rectangle
                {...rectProps}
                fill={primary?.isPending ? `url(#${patternIdPrimary})` : colorPrimary}
                cursor={dataPrimary.onClick ? "pointer" : "default"}
                onClick={() => dataPrimary.onClick?.(primary!)}
              />
            );
          }}
        />

        {dataSecondary && (
          <Bar
            dataKey="secondaryY"
            barSize={barWidth}
            shape={(props) => {
              const { secondary, ...rectProps } = props as React.ComponentProps<typeof Rectangle> & {
                secondary?: TSecondary;
              };

              return (
                <Rectangle
                  {...rectProps}
                  fill={secondary?.isPending ? `url(#${patternIdSecondary})` : colorSecondary}
                  cursor={dataSecondary.onClick ? "pointer" : "default"}
                  onClick={() => dataSecondary.onClick?.(secondary!)}
                />
              );
            }}
          />
        )}

        {dataBenchmark && (
          <Line
            type="linear"
            dataKey="benchmarkY"
            stroke={dataColors.benchmark}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            connectNulls={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function CustomTooltip<TPrimary extends BarChartDataPoint, TSecondary extends BarChartDataPoint>({
  active,
  payload,
  type,
  hoverLabel,
}: Partial<{
  active?: boolean;
  payload?: {
    dataKey: string;
    payload: {
      primary: TPrimary;
      secondary?: TSecondary;
      benchmark?: BarChartDataPoint;
      x: string;
    };
  }[];
}> & {
  type?: "date";
  hoverLabel?: Props<TPrimary, TSecondary>["hoverLabel"];
}) {
  const { i18n } = useTranslation();

  const first = payload?.[0];
  if (!active || !first) {
    return null;
  }

  const dataKey = first.dataKey;
  const isSecondary = dataKey === "secondaryY";

  return (
    <ChartTooltip>
      {type === "date" && first.payload.x ? (
        <span className="block">{formatDate(i18n, "monthYear", first.payload.x)}</span>
      ) : null}
      {hoverLabel?.(
        {
          x: first.payload.x,
          primary: first.payload.primary,
          secondary: first.payload.secondary,
          benchmark: first.payload.benchmark,
        },
        isSecondary,
      )}
    </ChartTooltip>
  );
}
